/**
 * Money put into the company, the shares issued for it, and who owns what.
 *
 * The screen is built around the one thing people get wrong: **money in is not
 * ownership**. A contribution is cash and a promise; an allotment is the legal
 * act. Between them runs a sixty-day clock, shown as a countdown, because on
 * day sixty the money stops being equity and becomes a debt — and that is an
 * entry, not a reminder.
 *
 * Ownership is asked for as at a date, never stored: "who owned what on the day
 * of that resolution" is the question, and only a derivation answers it.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { useAuth } from '../auth'
import { Card, ErrorNote, Field, Loading, Note, Pill, Stat } from '../components/ui'

interface Option {
  id: string
  name: string
}

interface FormOptions {
  departments: Option[]
  locations: Option[]
  vendors: Option[]
  my_department_id: string | null
  my_location_id: string | null
}

interface ShareClass {
  id: string
  code: string
  name: string
  kind: string
  face_value_paise: number
  votes_per_share: number
  is_active: boolean
  authorised_units: number
  issued_units: number
  headroom_units: number
}

interface Contribution {
  id: string
  reference: string
  investor_party_id: string
  investor_name: string
  purpose: string
  intended_share_class_id: string | null
  amount_paise: number
  expected_on: string
  received_on: string | null
  allotment_due_on: string | null
  refund_due_on: string | null
  days_left: number | null
  allotted_paise: number
  refunded_paise: number
  unallotted_paise: number
  status: string
  approval_state: string
  notes: string | null
  expected_receipt_reference: string | null
  refund_payment_reference: string | null
}

interface AllotmentLine {
  holder_party_id: string
  holder_name: string
  units: number
  issue_price_paise: number
  face_value_paise: number
  consideration: string
}

interface Allotment {
  id: string
  reference: string
  allotted_on: string
  share_class: string
  allotment_type: string
  units: number
  capital_paise: number
  premium_paise: number
  total_paise: number
  status: string
  approval_state: string
  pas3_due_on: string
  pas3_filed_on: string | null
  reason: string | null
  lines: AllotmentLine[]
}

interface Transfer {
  id: string
  reference: string
  transfer_date: string
  share_class: string
  from_name: string
  to_name: string
  units: number
  consideration_paise: number
  status: string
}

interface Ownership {
  as_of: string
  total_units: number
  total_votes: number
  holders: {
    party_id: string
    party_name: string
    share_class: string
    units: number
    capital_paise: number
    share_of_class_bp: number
    share_of_votes_bp: number
  }[]
  by_class: {
    share_class: string
    issued_units: number
    authorised_units: number
  }[]
}

interface CapitalSummary {
  held_paise: number
  contributions_open: number
  due_for_reclassification: { reference: string; amount_paise: number; allot_by: string }[]
  in_breach: { reference: string; amount_paise: number; refund_by: string }[]
  pas3_overdue: { reference: string; due_on: string }[]
  ownership: Ownership
}

const PURPOSES = [
  ['share_application_pending_allotment', 'For shares, to be allotted within 60 days'],
  ['share_subscription', 'For shares, being allotted now'],
  ['capital_contribution_no_allotment', 'A contribution with no shares to follow'],
  ['partner_capital', "A partner's capital"],
  ['proprietor_capital', "The proprietor's capital"],
] as const

const ALLOTMENT_TYPES = [
  ['private_placement', 'Private placement'],
  ['preferential', 'Preferential issue'],
  ['rights', 'Rights issue'],
  ['bonus', 'Bonus issue'],
  ['incorporation', 'On incorporation'],
  ['opening_register', 'Opening register (history, not money)'],
] as const

function toPaise(text: string): number {
  const clean = text.replace(/[^0-9.-]/g, '')
  if (!clean) return 0
  return Math.round(Number(clean) * 100)
}

function percent(bp: number): string {
  return `${(bp / 100).toFixed(2).replace(/\.00$/, '')}%`
}

const today = () => new Date().toISOString().slice(0, 10)

export default function Capital() {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const [asOf, setAsOf] = useState(today())
  const [drafting, setDrafting] = useState(false)
  const [allotting, setAllotting] = useState<Contribution | null>(null)

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['capital'] })

  const summary = useQuery({
    queryKey: ['capital', 'summary'],
    queryFn: () => api.get<CapitalSummary>('/financing/capital-summary'),
  })
  const classes = useQuery({
    queryKey: ['capital', 'classes'],
    queryFn: () => api.get<ShareClass[]>('/financing/share-classes'),
  })
  const contributions = useQuery({
    queryKey: ['capital', 'contributions'],
    queryFn: () => api.get<Contribution[]>('/financing/contributions'),
  })
  const allotments = useQuery({
    queryKey: ['capital', 'allotments'],
    queryFn: () => api.get<Allotment[]>('/financing/allotments'),
  })
  const transfers = useQuery({
    queryKey: ['capital', 'transfers'],
    queryFn: () => api.get<Transfer[]>('/financing/transfers'),
  })
  const ownership = useQuery({
    queryKey: ['capital', 'ownership', asOf],
    queryFn: () => api.get<Ownership>(`/financing/ownership?as_of=${asOf}`),
  })

  const submit = useMutation({
    mutationFn: (id: string) => api.post(`/financing/contributions/${id}/submit`),
    onSuccess: refresh,
  })
  const reclassify = useMutation({
    mutationFn: () => api.post<{ count: number }>('/financing/contributions/reclassify'),
    onSuccess: refresh,
  })
  const refund = useMutation({
    mutationFn: (id: string) =>
      api.post<{ reference: string }>(`/financing/contributions/${id}/refund`),
    onSuccess: refresh,
  })
  const submitAllotment = useMutation({
    mutationFn: (id: string) => api.post(`/financing/allotments/${id}/submit`),
    onSuccess: refresh,
  })

  if (summary.isLoading) return <Loading what="Loading the capital position" />
  const figures = summary.data

  return (
    <>
      <div className="grid">
        <Stat
          label="Held, not yet allotted"
          value={formatInr(figures?.held_paise ?? 0)}
          note={`${figures?.contributions_open ?? 0} contribution(s) open`}
        />
        <Stat
          label="Shares issued"
          value={(figures?.ownership.total_units ?? 0).toLocaleString('en-IN')}
          note={`${figures?.ownership.holders.length ?? 0} holder(s)`}
        />
        <Stat
          label="Past 60 days"
          value={figures?.due_for_reclassification.length ?? 0}
          note="stops being equity until reclassified"
        />
        <Stat
          label="Filings overdue"
          value={figures?.pas3_overdue.length ?? 0}
          note="PAS-3 is due 15 days after an allotment"
        />
      </div>

      {(figures?.due_for_reclassification.length ?? 0) > 0 && (
        <Card
          title="Share money past its sixty days"
          tight
          actions={
            can('fin.equity.create') && (
              <button
                className="small primary"
                disabled={reclassify.isPending}
                onClick={() => reclassify.mutate()}
              >
                Reclassify it
              </button>
            )
          }
        >
          <ErrorNote error={reclassify.error} />
          <Note kind="warn">
            Money received for shares is equity only while allotment is still due. Past sixty days
            it is a debt, repayable within fifteen more — and a month cannot close until the books
            say so.
          </Note>
          <table>
            <thead>
              <tr>
                <th>Contribution</th>
                <th className="num">Amount</th>
                <th>Should have been allotted by</th>
              </tr>
            </thead>
            <tbody>
              {(figures?.due_for_reclassification ?? []).map((item) => (
                <tr key={item.reference}>
                  <td className="mono">{item.reference}</td>
                  <td className="num">{formatInr(item.amount_paise)}</td>
                  <td className="date">{item.allot_by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {(figures?.in_breach.length ?? 0) > 0 && (
        <Card title="Money that should have been refunded" tight>
          <Note kind="error">
            Share money not allotted and not refunded within seventy-five days is a prohibited
            deposit, and it carries interest at 12% a year until it goes back. Refund it.
          </Note>
          <table>
            <thead>
              <tr>
                <th>Contribution</th>
                <th className="num">Amount</th>
                <th>Refund was due</th>
              </tr>
            </thead>
            <tbody>
              {(figures?.in_breach ?? []).map((item) => (
                <tr key={item.reference}>
                  <td className="mono">{item.reference}</td>
                  <td className="num">{formatInr(item.amount_paise)}</td>
                  <td className="date">{item.refund_by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Card
        title="Money put in"
        hint={`${(contributions.data ?? []).length}`}
        tight
        actions={
          can('fin.equity.create') && (
            <button className="small" onClick={() => setDrafting(!drafting)}>
              {drafting ? 'Close' : 'Record a contribution'}
            </button>
          )
        }
      >
        <ErrorNote error={contributions.error ?? submit.error ?? refund.error} />
        {drafting && (
          <ContributionForm
            classes={classes.data ?? []}
            onDone={() => {
              setDrafting(false)
              refresh()
            }}
          />
        )}
        {(contributions.data ?? []).length === 0 ? (
          <div className="empty">Nothing has been put in yet.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Reference</th>
                <th>Investor</th>
                <th>What for</th>
                <th className="num">Amount</th>
                <th className="num">Unallotted</th>
                <th>Clock</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(contributions.data ?? []).map((row) => (
                <tr key={row.id}>
                  <td className="mono">{row.reference}</td>
                  <td>{row.investor_name}</td>
                  <td className="muted">{row.purpose.replace(/_/g, ' ')}</td>
                  <td className="num">{formatInr(row.amount_paise)}</td>
                  <td className="num">{formatInr(row.unallotted_paise)}</td>
                  <td>
                    {row.received_on ? (
                      <>
                        <div className="date">{row.allotment_due_on}</div>
                        <div className={row.days_left !== null && row.days_left < 15 ? '' : 'faint'}>
                          {row.days_left !== null && row.days_left >= 0
                            ? `${row.days_left} day(s) left`
                            : 'overdue'}
                        </div>
                      </>
                    ) : (
                      <span className="faint">not received yet</span>
                    )}
                  </td>
                  <td>
                    <Pill value={row.status} />
                    {row.status === 'pending_approval' && (
                      <div className="faint">{row.approval_state}</div>
                    )}
                  </td>
                  <td className="num">
                    {(row.status === 'draft' || row.status === 'returned') &&
                      can('fin.equity.submit') && (
                        <button className="small" onClick={() => submit.mutate(row.id)}>
                          Submit
                        </button>
                      )}
                    {row.unallotted_paise > 0 &&
                      ['received', 'partially_allotted', 'refund_due', 'deposit_breach'].includes(
                        row.status,
                      ) && (
                        <>
                          {can('fin.equity.create') && (
                            <button className="small primary" onClick={() => setAllotting(row)}>
                              Allot
                            </button>
                          )}{' '}
                          {can('spend.payment_request.create') && (
                            <button className="small" onClick={() => refund.mutate(row.id)}>
                              Refund
                            </button>
                          )}
                        </>
                      )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {allotting && (
        <AllotmentForm
          contribution={allotting}
          classes={classes.data ?? []}
          onDone={() => {
            setAllotting(null)
            refresh()
          }}
        />
      )}

      <Card title="Shares issued" hint={`${(allotments.data ?? []).length}`} tight>
        <ErrorNote error={allotments.error ?? submitAllotment.error} />
        {(allotments.data ?? []).length === 0 ? (
          <div className="empty">No shares have been issued yet.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Reference</th>
                <th>Date</th>
                <th>Class</th>
                <th>Kind</th>
                <th className="num">Shares</th>
                <th className="num">Capital</th>
                <th className="num">Premium</th>
                <th>PAS-3</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(allotments.data ?? []).map((row) => (
                <tr key={row.id}>
                  <td className="mono">{row.reference}</td>
                  <td className="date">{row.allotted_on}</td>
                  <td className="muted">{row.share_class}</td>
                  <td className="muted">{row.allotment_type.replace(/_/g, ' ')}</td>
                  <td className="num">{row.units.toLocaleString('en-IN')}</td>
                  <td className="num">{formatInr(row.capital_paise)}</td>
                  <td className="num">{formatInr(row.premium_paise)}</td>
                  <td className="muted date">
                    {row.pas3_filed_on ?? (
                      <span className="faint">due {row.pas3_due_on}</span>
                    )}
                  </td>
                  <td>
                    <Pill value={row.status} />
                    {row.status === 'pending_approval' && (
                      <div className="faint">{row.approval_state}</div>
                    )}
                  </td>
                  <td className="num">
                    {(row.status === 'draft' || row.status === 'returned') &&
                      can('fin.equity.submit') && (
                        <button
                          className="small"
                          onClick={() => submitAllotment.mutate(row.id)}
                        >
                          Submit
                        </button>
                      )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card
        title="Who owns the company"
        hint={ownership.data ? `as at ${ownership.data.as_of}` : ''}
        actions={
          <label style={{ fontSize: 12 }}>
            as at{' '}
            <input type="date" value={asOf} onChange={(event) => setAsOf(event.target.value)} />
          </label>
        }
        tight
      >
        <ErrorNote error={ownership.error} />
        <Note>
          Ownership is worked out from the allotments and transfers up to the date you pick, not
          stored. Ask about the day of a board resolution and you get the register as it stood
          then.
        </Note>
        {(ownership.data?.holders.length ?? 0) === 0 ? (
          <div className="empty">Nobody held shares on that date.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Holder</th>
                <th>Class</th>
                <th className="num">Shares</th>
                <th className="num">Face value</th>
                <th className="num">Of the class</th>
                <th className="num">Of the votes</th>
              </tr>
            </thead>
            <tbody>
              {(ownership.data?.holders ?? []).map((row) => (
                <tr key={`${row.party_id}-${row.share_class}`}>
                  <td>{row.party_name}</td>
                  <td className="muted">{row.share_class}</td>
                  <td className="num">{row.units.toLocaleString('en-IN')}</td>
                  <td className="num">{formatInr(row.capital_paise)}</td>
                  <td className="num">{percent(row.share_of_class_bp)}</td>
                  <td className="num">{percent(row.share_of_votes_bp)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="Share classes" hint={`${(classes.data ?? []).length}`} tight>
        <ErrorNote error={classes.error} />
        {(classes.data ?? []).length === 0 ? (
          <div className="empty">No share classes recorded yet.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th className="num">Face value</th>
                <th className="num">Votes</th>
                <th className="num">Authorised</th>
                <th className="num">Issued</th>
                <th className="num">Headroom</th>
              </tr>
            </thead>
            <tbody>
              {(classes.data ?? []).map((row) => (
                <tr key={row.id}>
                  <td className="mono">{row.code}</td>
                  <td>{row.name}</td>
                  <td className="num">{formatInr(row.face_value_paise)}</td>
                  <td className="num">{row.votes_per_share}</td>
                  <td className="num">{row.authorised_units.toLocaleString('en-IN')}</td>
                  <td className="num">{row.issued_units.toLocaleString('en-IN')}</td>
                  <td className="num">{row.headroom_units.toLocaleString('en-IN')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {(transfers.data ?? []).length > 0 && (
        <Card title="Shares changing hands" hint={`${(transfers.data ?? []).length}`} tight>
          <Note>
            A transfer moves shares between two people. No company money is involved, so there is
            no entry — only the register changes.
          </Note>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>From</th>
                <th>To</th>
                <th>Class</th>
                <th className="num">Shares</th>
                <th className="num">Consideration</th>
              </tr>
            </thead>
            <tbody>
              {(transfers.data ?? []).map((row) => (
                <tr key={row.id}>
                  <td className="date">{row.transfer_date}</td>
                  <td>{row.from_name}</td>
                  <td>{row.to_name}</td>
                  <td className="muted">{row.share_class}</td>
                  <td className="num">{row.units.toLocaleString('en-IN')}</td>
                  <td className="num">{formatInr(row.consideration_paise)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Recording money in
// ---------------------------------------------------------------------------

function ContributionForm({
  classes,
  onDone,
}: {
  classes: ShareClass[]
  onDone: () => void
}) {
  const options = useQuery({
    queryKey: ['spend-form-options'],
    queryFn: () => api.get<FormOptions>('/spend/form-options'),
  })
  const [form, setForm] = useState({
    investor_party_id: '',
    purpose: 'share_application_pending_allotment',
    amount: '',
    expected_on: today(),
    intended_share_class_id: classes[0]?.id ?? '',
    notes: '',
  })
  const set = (key: string, value: string) => setForm((f) => ({ ...f, [key]: value }))

  const create = useMutation({
    mutationFn: () =>
      api.post('/financing/contributions', {
        investor_party_id: form.investor_party_id,
        purpose: form.purpose,
        amount_paise: toPaise(form.amount),
        expected_on: form.expected_on,
        intended_share_class_id: form.purpose.startsWith('share')
          ? form.intended_share_class_id
          : null,
        location_id: options.data?.my_location_id,
        department_id: options.data?.my_department_id,
        notes: form.notes || null,
      }),
    onSuccess: onDone,
  })

  return (
    <Card title="Money being put into the company">
      <ErrorNote error={create.error ?? options.error} />
      <Note>
        The purpose is required, because money in does not by itself change who owns the company —
        and each purpose is a different entry. Money for shares starts a sixty-day clock the moment
        it arrives.
      </Note>
      <div className="row">
        <Field label="Investor">
          <select
            value={form.investor_party_id}
            onChange={(event) => set('investor_party_id', event.target.value)}
          >
            <option value="">Choose…</option>
            {(options.data?.vendors ?? []).map((party) => (
              <option key={party.id} value={party.id}>
                {party.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="What the money is for">
          <select value={form.purpose} onChange={(event) => set('purpose', event.target.value)}>
            {PURPOSES.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Amount">
          <input value={form.amount} onChange={(event) => set('amount', event.target.value)} />
        </Field>
        <Field label="Expected on">
          <input
            type="date"
            value={form.expected_on}
            onChange={(event) => set('expected_on', event.target.value)}
          />
        </Field>
        {form.purpose.startsWith('share') && (
          <Field label="Class of shares">
            <select
              value={form.intended_share_class_id}
              onChange={(event) => set('intended_share_class_id', event.target.value)}
            >
              {classes.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.code} — {formatInr(row.face_value_paise)} each
                </option>
              ))}
            </select>
          </Field>
        )}
      </div>
      <Field
        label="Note"
        caption="required when money comes in with no shares to follow: say why it is not a loan"
      >
        <input value={form.notes} onChange={(event) => set('notes', event.target.value)} />
      </Field>
      <button
        className="primary"
        disabled={create.isPending || !form.investor_party_id || !form.amount}
        onClick={() => create.mutate()}
      >
        Save as a draft
      </button>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Issuing the shares
// ---------------------------------------------------------------------------

function AllotmentForm({
  contribution,
  classes,
  onDone,
}: {
  contribution: Contribution
  classes: ShareClass[]
  onDone: () => void
}) {
  const share =
    classes.find((row) => row.id === contribution.intended_share_class_id) ?? classes[0]
  const [units, setUnits] = useState('')
  const [price, setPrice] = useState(share ? (share.face_value_paise / 100).toString() : '')
  const [type, setType] = useState('private_placement')
  const [when, setWhen] = useState(today())
  const [reason, setReason] = useState('')

  const create = useMutation({
    mutationFn: () =>
      api.post('/financing/allotments', {
        allotted_on: when,
        share_class_id: share?.id,
        allotment_type: type,
        reason: reason || null,
        lines: [
          {
            holder_party_id: contribution.investor_party_id,
            units: Number(units || 0),
            issue_price_paise: toPaise(price),
            consideration: 'cash_contribution',
            capital_contribution_id: contribution.id,
          },
        ],
      }),
    onSuccess: onDone,
  })

  const total = Number(units || 0) * toPaise(price)
  const capital = Number(units || 0) * (share?.face_value_paise ?? 0)

  return (
    <Card title={`Issue shares against ${contribution.reference}`}>
      <ErrorNote error={create.error} />
      <Note>
        {formatInr(contribution.unallotted_paise)} of {contribution.investor_name}’s money is
        unallotted. Face value goes to share capital and anything above it to securities premium,
        which the law restricts to particular uses.
      </Note>
      <div className="row">
        <Field label="Shares">
          <input value={units} onChange={(event) => setUnits(event.target.value)} />
        </Field>
        <Field label="Price each" caption={`face value ${formatInr(share?.face_value_paise ?? 0)}`}>
          <input value={price} onChange={(event) => setPrice(event.target.value)} />
        </Field>
        <Field label="Kind of issue">
          <select value={type} onChange={(event) => setType(event.target.value)}>
            {ALLOTMENT_TYPES.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Allotted on">
          <input type="date" value={when} onChange={(event) => setWhen(event.target.value)} />
        </Field>
      </div>
      <Field label="Board resolution" caption="what authorised it">
        <input value={reason} onChange={(event) => setReason(event.target.value)} />
      </Field>
      {total > 0 && (
        <Note kind={total > contribution.unallotted_paise ? 'warn' : 'info'}>
          {formatInr(total)} in all: {formatInr(capital)} of share capital and{' '}
          {formatInr(total - capital)} of premium.
          {total > contribution.unallotted_paise &&
            ' That is more than the money held against this contribution.'}
        </Note>
      )}
      <button
        className="primary"
        disabled={create.isPending || !units || !price || !share}
        onClick={() => create.mutate()}
      >
        Prepare the allotment
      </button>
    </Card>
  )
}
